import { Box } from '@mui/material';
import { MainTabType, MAIN_TABS, ALL_TABS, MediaType, isTabAvailable } from './profileTabs';
import { UserProfile } from '../../types/UserProfile';

/**
 * プロフィールのタブに出す「中身があるか」の視覚表現。
 *
 * - 件数を見出しの右上に小さく添える
 * - 0件のタブは淡くする（**他人のプロフィールだけ**。自分のページで
 *   自分の未着手のタブが淡いのは、機能が使えないように見えて損なだけ）
 *
 * **淡くするだけで、押せなくはしないこと。** タブの活性を件数に連動させると
 * `resolveTab` が「URL で来たタブが押せるか」を件数の到着前に判定できず、
 * 表示後に選択中のタブが無効化される事故になる。空であることは開けば分かる。
 */

/** タブ幅が狭いので4桁以上は詰める。1125 -> 1.1k */
export const formatTabCount = (n: number): string => {
  if (n < 1000) return String(n);
  if (n < 10_000) {
    const k = (n / 1000).toFixed(1);
    return `${k.endsWith('.0') ? k.slice(0, -2) : k}k`;
  }
  return `${Math.round(n / 1000)}k`;
};

/**
 * **数字は出さないが、0件かどうかの判定には件数を使うタブ。**
 *
 * - フォロー   : 繋がりが有るか無いかだけ分かればよく、人数そのものは
 *                タブに要る情報ではない（見たければ開けば出る）
 * - ライブラリ : 開いた先にステータス別の内訳があり、そこが件数の置き場
 *
 * どちらも**淡くする判定からは外さないこと。** 空なら淡いという規則が
 * 一部のタブだけ効かないと、淡くないことが「中身がある」の意味を持たなくなる。
 */
const COUNT_HIDDEN_TABS: MainTabType[] = [MAIN_TABS.FOLLOWS, MAIN_TABS.LIBRARY];

/**
 * そのタブが持っている件数。`null` は「件数という概念が無いタブ」。
 *
 * **表示用ではなく判定用。** 淡くするかどうかはこの値で決める。
 * 数字として出してよいかは tabBadgeCount を通すこと。
 *
 * ここに出す数は**そのタブを開いたときの一覧の件数と一致させること**。
 * サーバー側の絞り込みは games/profile_tab_counts.py に対応が書いてある。
 */
export const tabCountFor = (
  tab: MainTabType,
  media: MediaType,
  profile: UserProfile | undefined,
): number | null => {
  // 件数という概念が無いタブ。淡くもしない
  //   プロフィール : 常に中身がある
  //   アクティビティ: 件数に意味が薄い
  //   タイムライン  : アイコンのタブ
  if (tab === MAIN_TABS.PROFILE || tab === MAIN_TABS.ACTIVITY || tab === MAIN_TABS.TIMELINE) {
    return null;
  }

  // ライブラリだけは元から応答にある status_counts で判定できる。
  // tab_counts の有無より前に返すこと（旧いキャッシュでも淡さは正しく出る）
  if (tab === MAIN_TABS.LIBRARY) return sumStatusCounts(profile, media);

  const counts = profile?.tab_counts;
  // 旧いキャッシュ（tab_counts を持たない応答）でも落ちないように
  if (!counts) return null;

  switch (tab) {
    // レビューはメディアごとに別のテーブル
    case MAIN_TABS.REVIEWS:
      if (media === 'anime') return counts.anime_reviews;
      if (media === 'movie') return counts.movie_reviews;
      return counts.reviews;

    case MAIN_TABS.LISTS: return counts.lists;
    case MAIN_TABS.MEMORIES: return counts.memories;
    case MAIN_TABS.PLAYLOGS: return counts.playlogs;
    case MAIN_TABS.POSTS: return counts.posts;
    case MAIN_TABS.GOTY: return counts.goty;
    case MAIN_TABS.GALLERY: return counts.gallery;
    // フォローは「その人に繋がりがあるか」なので両方向の合計で見る
    case MAIN_TABS.FOLLOWS: return counts.followers + counts.following;
    default: return null;
  }
};

const STATUS_COUNT_KEY: Record<MediaType, keyof UserProfile> = {
  game: 'status_counts',
  anime: 'anime_status_counts',
  movie: 'movie_status_counts',
};

/** ライブラリの登録総数。数字としては出さず、空かどうかの判定にだけ使う */
const sumStatusCounts = (profile: UserProfile | undefined, media: MediaType): number | null => {
  if (!profile) return null;
  const raw = profile[STATUS_COUNT_KEY[media]];
  if (!Array.isArray(raw)) return null;
  return raw.reduce((a: number, b) => a + (Number(b) || 0), 0);
};

/**
 * 見本として出す数字。装飾パネルで試着している間だけ使う。
 * 実在しない数だと分かるよう、桁は詰まっているが切りの悪い値にしてある。
 */
export const SAMPLE_TAB_COUNT = 99;

/**
 * タブに**数字として出す**件数。出さないなら null。
 *
 * 0 は出さない。「0」と書かれていても淡さ以上のことは分からないうえ、
 * 空のタブほど数字が目に入る形になって置き場所として逆。
 *
 * @param preview 見本の数字を出すか。**呼び出し側で hasAnyTabBadge を見て決めること。**
 *   ここは 0 件かどうかしか見ないので、真を渡せば 0 件のタブすべてに見本が出る。
 *   1つでも実数を持っている人に混ぜてはいけない（下の hasAnyTabBadge 参照）。
 */
export const tabBadgeCount = (
  tab: MainTabType,
  media: MediaType,
  profile: UserProfile | undefined,
  preview = false,
): number | null => {
  if (COUNT_HIDDEN_TABS.includes(tab)) return null;
  const count = tabCountFor(tab, media, profile);
  if (count === null) return null;
  if (count > 0) return count;
  return preview ? SAMPLE_TAB_COUNT : null;
};

/**
 * 数字が1つでも出るか。**見本を出してよいのはこれが false のときだけ。**
 *
 * 見本は「アクセントスタイルの効果が見える対象が1つも無い」状態を埋めるためのもの。
 * 実数を1つでも持っている人は、その数字で効果を確認できるので見本は要らない。
 * そこへ 0 件のタブだけ 99 を混ぜると、**実数と見本が同じ行に並んで区別がつかない**
 * （レビュー32 の隣にメモリー99、では 99 が本物に見える）。
 *
 * 判定はバッジを出しうるタブだけで行う。フォローとライブラリは
 * 元から数字を置かない場所なので、そこに件数があっても「数字が出ている」ことにはならない。
 */
export const hasAnyTabBadge = (media: MediaType, profile: UserProfile | undefined): boolean =>
  ALL_TABS.some(tab => {
    // **そのメディアで出ないタブは数えないこと。** 見本を出すかどうかは
    // 「今そこに並んでいるタブに数字があるか」で決まる。ゲームのポスト数を
    // 理由にアニメのタブ列で見本を止めると、アニメ側だけ効果が見えなくなる。
    // 装飾パネルは自分のページ専用なので isPublic は false 固定でよい。
    if (!isTabAvailable(tab, media, false)) return false;
    if (COUNT_HIDDEN_TABS.includes(tab)) return false;
    const count = tabCountFor(tab, media, profile);
    return count !== null && count > 0;
  });

/**
 * 件数バッジ付きのタブ見出し。
 *
 * @param accent アクセントスタイルが「強め」のときだけ true。
 *   件数はアクセントを乗せても意味が壊れない要素（順位や状態を表さない）なので
 *   強めの対象に入れている。`primary.main` はプロフィール側の ThemeProvider で
 *   所有者のアクセント色に差し替わっているので、**色を直接渡さないこと**。
 */
export const ProfileTabLabel = ({
  label,
  count,
  accent = false,
}: {
  label: string;
  count: number | null;
  accent?: boolean;
}) => {
  if (count === null) return <>{label}</>;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: '2px' }}>
      {label}
      <Box
        component="span"
        sx={{
          // em にしてタブ側のレスポンシブな文字サイズに追従させる
          fontSize: '0.75em',
          lineHeight: 1,
          mt: '-0.1em',
          // アクセントを乗せるときは色で十分に目立つので、薄めるのをやめる
          ...(accent ? { color: 'primary.main' } : { opacity: 0.75 }),
          // 桁が変わっても幅が踊らないように
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatTabCount(count)}
      </Box>
    </Box>
  );
};
